import { readParquetFile } from "./parquet-utils";

/**
 * Utility functions for processing Parquet files.
 * Reference to repo: https://github.com/noworneverev/graphrag-visualizer
 */

const baseMapping = {
  "entities.parquet": "entity",
  "relationships.parquet": "relationship",
  "documents.parquet": "document",
  "text_units.parquet": "text_unit",
  "communities.parquet": "community",
  "community_reports.parquet": "community_report",
  "covariates.parquet": "covariate",
};

const fileSchemas = {};
Object.entries(baseMapping).forEach(([key, value]) => {  
  fileSchemas[key] = value;  
  fileSchemas[`create_final_${key}`] = value;
});

function checkFiles (files) {
  // Convert FileList to an array and extract file names
  const filesArray = Array.from(files);
  const fileNames = filesArray.map(file => file.name);
  const fileTypes = fileNames.map((fileName) => fileSchemas[fileName] || null).filter(Boolean);
  if (fileTypes.length < 6) {
    throw new Error(
      "One or more required files are missing. Please include entities, relationships, text-units, communities, community reports, and documents."
    );
  }
}

/**
 * Loads a set of parquet files for one graph and processes them into an ArrayMap - a list of graph elements represented in arrays.
 * @param {Array} files - An array of file paths or File objects (file names).
 * @returns {Object} ArrayMap: A list of arrays, each representing a file. Array format different based on file type - see documentation for details.
 * @throws {Error} If any required files are missing.
 */
async function loadFiles (files) {
    let entitiesArray = [];
    let relationshipsArray = [];
    let documentsArray = [];
    let textUnitsArray = [];
    let communitiesArray = [];
    let communityReportsArray = [];
    let covariatesArray = [];
    const arrayMap = {
            entity: entitiesArray,
            relationship: relationshipsArray,
            document: documentsArray,
            text_unit: textUnitsArray,
            community: communitiesArray,
            community_report: communityReportsArray,
            covariate: covariatesArray,
        };

    for (const file of files) {
        const fileName = typeof file === "string" ? file.split("/").pop() : file.name;  //extract file name from path or use file.name directly
        const schema = fileSchemas[fileName]; //map to type of file

        let data;
        if (typeof file === "string") {
            data = await readParquetFile(file, schema);
        }

        if (schema && arrayMap[schema]) {
            arrayMap[schema].push(data);
            arrayMap[schema] = arrayMap[schema].flat();
        }
    }

    if (
        entitiesArray.length === 0 ||
        relationshipsArray.length === 0 ||
        textUnitsArray.length === 0 ||
        communitiesArray.length === 0 ||
        communityReportsArray.length === 0 ||
        documentsArray.length === 0
    ) {
        throw new Error(
            "One or more required files are missing: entities, relationships, text-units, communities, community reports, or documents."
        );
    }
    return arrayMap;
};

/**
 * The following are a list of getters that extracts specific data from the ArrayMap and set it using the provided setter functions.
 */

/**
 * Gets text units with given IDs and sets the state.
 * @param {*} tu_ids - An array of text unit IDs to fetch.
 * @param {*} setTextUnit - A setter function to update the text unit state.
 * @param {*} details - A Details object (arrayMap but without entities and relationships) from the selected Graph, containing text units.
 */
function getTextUnits(tu_ids, setTextUnit, details) {
    try {
      const tu_contents = tu_ids
      .map(id => {
        const tu = details.text_units?.find(t => t.id === id);
        if (!tu) {
          console.warn(`Text unit with ID ${id} not found.`);
          return null;
        }
        return tu.text || `Text unit with ID ${id} has no content.`;
      })
      .filter(Boolean)
      setTextUnit(tu_contents);
    } catch (error) {
      console.error("Error fetching text unit details:", error);
      setTextUnit(["No text unit details."]);
    }
}

/**
 * Gets links with given IDs.
 * @param {*} link_ids - An array of link IDs to fetch.
 * @param {*} links - The array of all link objects.
 * @returns {Array} - An array of links matching the given IDs.
 */
function getLinks(link_ids, links) {
    if (!links || !Array.isArray(links)) {
        console.warn("Links data is not available or not an array.");
        return [];  
    }
    try {
      const filtered_links = link_ids
      .map(id => {
        const link = links.find(l => l.id === id);
        return link;
      })
      .filter(Boolean)
      return filtered_links;
    } catch (error) {
      console.error("Error fetching link:", error);
      return [];
    }
}

/**
 * Gets nodes with given IDs.
 * @param {*} node_ids - An array of node IDs to fetch.
 * @param {*} nodes - The array of all node objects.
 * @returns {Array} - An array of nodes matching the given IDs.
 */
function getNodes(node_ids, nodes) {
    if (!nodes || !Array.isArray(nodes)) {
        console.warn("Nodes data is not available or not an array.");
        return [];  
    }
    try {
      const filtered_nodes = node_ids
      .map(id => {
        const node = nodes.find(n => n.uuid === id);
        return node;
      })
      .filter(Boolean)
      return filtered_nodes;
    } catch (error) {
      console.error("Error fetching node:", error);
      return [];
    }
}

/**
 * Gets the community report for a given community ID.
 * @param {*} community_id - The ID of the community to fetch the report for.
 * @param {*} community_reports - The array of all community report objects.
 * @returns {Object|null} - The community report object if found, otherwise null.
 */
function getCommunityReport(community_id, community_reports) {
  if (!community_reports || !Array.isArray(community_reports)) {
      console.warn("Community reports data is not available or not an array.");
      return null;    
  }
  try {
    const report = community_reports.find(cr => cr.community === community_id);
    if (!report) {
      throw new Error(`Community report for ID ${community_id} not found.`);
    }
    return report;
  } catch (error) {
    console.error("Error fetching community report:", error);
    return null;
  }
}

export { loadFiles, getTextUnits, getLinks, getNodes, getCommunityReport, checkFiles };